export type PersonalInfoContext = {
  readonly name?: string;
  readonly companyName?: string;
  readonly email?: string;
};

export type PasswordRuleResults = {
  readonly hasDigit: boolean;
  readonly hasLowercase: boolean;
  readonly hasUppercase: boolean;
  readonly hasNonAlphanumeric: boolean;
  readonly hasMinLength: boolean;
  readonly hasNoNameOrEmail: boolean;
  readonly hasNoCompanyName: boolean;
  readonly hasNoAngleBrackets: boolean;
  readonly hasNoCommonWords: boolean;
};

export type PasswordChecklistItem = {
  readonly id: keyof PasswordRuleResults;
  readonly label: string;
};

export const COMMON_FORBIDDEN_PATTERNS = [
  'admin',
  'user',
  'test',
  'demo',
  'password',
  '1234',
  ' or ',
  'secret',
  'pwd',
] as const;

export const PASSWORD_CHECKLIST_ITEMS: readonly PasswordChecklistItem[] = [
  { id: 'hasDigit', label: 'At least one digit (0-9)' },
  { id: 'hasLowercase', label: 'At least one lowercase (a-z)' },
  { id: 'hasUppercase', label: 'At least one uppercase (A-Z)' },
  { id: 'hasNonAlphanumeric', label: 'At least one non-alphanumeric character' },
  { id: 'hasMinLength', label: 'At least 8 characters' },
  { id: 'hasNoNameOrEmail', label: 'Must not contain your name or email address' },
  { id: 'hasNoCompanyName', label: 'Must not contain your company name' },
  { id: 'hasNoAngleBrackets', label: 'Must not contain < or >' },
  { id: 'hasNoCommonWords', label: 'Must not contain common words (e.g., "admin", "1234")' },
];

function containsValue(value: string, searchValue?: string): boolean {
  if (!searchValue) {
    return false;
  }

  const normalized = searchValue.trim().toLowerCase();

  return normalized.length > 2 && value.includes(normalized);
}

function containsNameOrEmail(passwordLowercased: string, info?: PersonalInfoContext): boolean {
  if (!info) {
    return false;
  }

  const emailIdentifier = info.email?.split('@')[0];
  const nameTokens = info.name
    ?.split(/\s+/)
    .map((token) => token.trim())
    .filter((token) => token.length > 2);

  return (
    containsValue(passwordLowercased, info.name) ||
    Boolean(nameTokens?.some((token) => containsValue(passwordLowercased, token))) ||
    containsValue(passwordLowercased, emailIdentifier)
  );
}

function containsCompanyName(passwordLowercased: string, info?: PersonalInfoContext): boolean {
  if (!info) {
    return false;
  }

  return containsValue(passwordLowercased, info.companyName);
}

function containsCommonWords(passwordLowercased: string): boolean {
  return COMMON_FORBIDDEN_PATTERNS.some((pattern) => passwordLowercased.includes(pattern));
}

export function evaluatePasswordRules(
  password: string,
  info?: PersonalInfoContext
): PasswordRuleResults {
  const passwordLowercased = password.toLowerCase();

  return {
    hasDigit: /\d/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasUppercase: /[A-Z]/.test(password),
    hasNonAlphanumeric: /[^\dA-Za-z]/.test(password),
    hasMinLength: password.length >= 8,
    hasNoNameOrEmail: !containsNameOrEmail(passwordLowercased, info),
    hasNoCompanyName: !containsCompanyName(passwordLowercased, info),
    hasNoAngleBrackets: !password.includes('<') && !password.includes('>'),
    hasNoCommonWords: !containsCommonWords(passwordLowercased),
  };
}

export function arePasswordRulesSatisfied(rules: PasswordRuleResults): boolean {
  return Object.values(rules).every(Boolean);
}
